/* eslint-disable import/no-cycle */
import React, { createContext } from 'react';
import PropTypes from 'prop-types';

import { checkChildren } from '@hooks';
import { mergeClasses } from '@utils';

import LinksHeading from './Links.Heading';
import LinksLink from './Links.Link';
import LinksLinkIcon from './Links.Link.Icon';

export const LinksContext = createContext();

/**
 * Links component that serves as a container for `Links.Heading` and `Links.Link` components.
 * Requires at least one child of type `Links.Heading` or `Links.Link`.
 *
 * @component Links
 * @param {Object} props - The properties for the Links component.
 * @param {React.ReactNode} props.children - The children of the Links component. Must include at least one `Links.Link` child component.
 * @param {string} [props.className] - Optional additional CSS classes to apply to the Links container.
 * @param {Object} [restProps] - Additional properties to be passed to the Links container.
 * @returns {ReactElement} The rendered Links component.
 * @example
 * <Links>
 *   <Links.Heading>Section Title</Links.Heading>
 *   <Links.Link href="/example1">Example 1</Links.Link>
 * </Links>
 */
export default function Links({ children, className, ...restProps }) {
  checkChildren(children, [Links.Heading, Links.Link]);

  if (!children) return null;

  return (
    <LinksContext.Provider value>
      <div className={mergeClasses('', className)} {...restProps}>
        <div className="container mx-auto">
          {React.Children.map(children, child => {
            if (child.type !== Links.Heading && child.type !== Links.Link) {
              throw new Error(
                'Links children must be of either type Links.Heading or Links.Link'
              );
            }
            return child;
          })}
        </div>
      </div>
    </LinksContext.Provider>
  );
}

Links.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string
};

Links.defaultProps = {
  className: ''
};

Links.Heading = LinksHeading;
Links.Link = LinksLink;
Links.Link.Icon = LinksLinkIcon;
